import { useState } from 'react';
import { CLASS_COLORS, enemies, getClass, getEnemy, towers } from '@prism/content';

import type { SaveData } from '../persistence/save.js';
import { enemyBrief } from './enemyBrief.js';
import { MenuParticles } from './MenuParticles.js';

/**
 * The Discovery log (playtest request): what you have fielded and what you have faced.
 *
 * The compendium exists because the game teaches by ambush — you meet a Drifter for the
 * first time when it is already over your wall, and afterwards there was nowhere to read
 * what it actually was. Everything here is the SAME authored data the simulation runs on,
 * so a page cannot describe a tower the game does not have.
 *
 * Wears the leaderboard's chrome deliberately (playtest request): the bloom, the drifting
 * motes, the skewed chips, the accent rule and the column table are this app's idea of a
 * full screen, and a third one inventing its own would read as a different program. The
 * shared rules are declared once in styles.css against both selectors rather than copied,
 * so the two cannot drift apart later.
 *
 * Locked rows stay listed. A compendium that hides what you have not met cannot tell you
 * how much is left, and "12 of 37" is most of why anyone opens one.
 */

type Tab = 'towers' | 'enemies' | 'bosses';

const TABS: { id: Tab; label: string }[] = [
  { id: 'towers', label: 'TOWERS' },
  { id: 'enemies', label: 'ENEMIES' },
  { id: 'bosses', label: 'BOSSES' },
];

const SUBTITLE: Record<Tab, string> = {
  towers: 'Every tower you have fielded. Click one for its numbers and its merge payoff.',
  enemies: 'Everything you have faced, and how many of it you have put down.',
  bosses: 'The ten that end a run. Click one to read what it does before it does it.',
};

interface Row {
  id: string;
  name: string;
  found: boolean;
  /** The three numeric columns, already formatted. `null` renders as a locked dash. */
  cells: [string, string, string];
  kills: number | null;
}

export function Discovery({ save, onBack }: { save: SaveData; onBack: () => void }) {
  const [tab, setTab] = useState<Tab>('towers');
  const [open, setOpen] = useState<string | null>(null);

  const known = new Set(save.discovery.towers);
  const met = save.discovery.enemyKills;

  const rows: Row[] =
    tab === 'towers'
      ? towers.map((t) => ({
          id: t.id,
          name: t.name,
          found: known.has(t.id),
          cells: [`${t.base.damage}`, `${t.base.attackInterval}s`, `${t.base.range}`],
          kills: null,
        }))
      : enemies
          .filter((e) => e.flags.includes('boss') === (tab === 'bosses'))
          .map((e) => ({
            id: e.id,
            name: e.name,
            found: e.id in met,
            cells: [`${e.hp}`, `${e.speed}`, `${e.leakDamage}`],
            kills: met[e.id] ?? 0,
          }));

  const foundCount = rows.filter((r) => r.found).length;
  const headings: [string, string, string] =
    tab === 'towers' ? ['DMG', 'RATE', 'RANGE'] : ['HP', 'SPEED', 'LEAK'];

  return (
    <div className="disc">
      <div className="menu__bloom" />
      <MenuParticles seed={5} />

      <div className="disc__head">
        <button type="button" className="disc__chip" onClick={onBack}>
          <span>&lsaquo; MENU</span>
        </button>

        <div className="disc__mark-title">
          <span className="disc__mark-bar" />
          <h2 className="disc__title">DISCOVERY</h2>
        </div>
        <span className="disc__sub">{SUBTITLE[tab]}</span>

        <div className="disc__mode">
          <span className="disc__mode-tag">
            <span>
              {foundCount} / {rows.length}
            </span>
          </span>
          <span className="disc__mode-note">found</span>
        </div>
      </div>

      <div className="disc__rule" />

      <div className="disc__tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`disc__chip${tab === t.id ? ' disc__chip--on' : ''}`}
            aria-pressed={tab === t.id}
            onClick={() => {
              setTab(t.id);
              setOpen(null);
            }}
          >
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="disc__table">
        <div className="disc__head-row">
          <span>NAME</span>
          <span>{headings[0]}</span>
          <span>{headings[1]}</span>
          <span>{headings[2]}</span>
          <span>{tab === 'towers' ? 'CLASSES' : 'KILLED'}</span>
        </div>
        <div className="disc__head-rule" />

        <div className="disc__rows">
          {rows.map((row) => (
            <div key={row.id}>
              <button
                type="button"
                className={`disc__row${row.found ? '' : ' disc__row--locked'}${
                  open === row.id ? ' disc__row--open' : ''
                }`}
                disabled={!row.found}
                title={row.found ? 'Open its page' : 'Not met yet'}
                onClick={() => setOpen(open === row.id ? null : row.id)}
              >
                <span className="disc__name">
                  {row.found ? row.name : <>&#128274; {row.name}</>}
                </span>
                {/* A locked row shows its name and nothing else: enough to know it is out
                    there, not enough to plan around a thing you have never met. */}
                <span className="disc__num">{row.found ? row.cells[0] : '—'}</span>
                <span className="disc__num">{row.found ? row.cells[1] : '—'}</span>
                <span className="disc__num">{row.found ? row.cells[2] : '—'}</span>
                <span className="disc__last">
                  {!row.found ? '—' : row.kills !== null ? row.kills : <Classes id={row.id} />}
                </span>
              </button>
              {open === row.id &&
                (tab === 'towers' ? <TowerPage id={row.id} /> : <EnemyPage id={row.id} />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Classes({ id }: { id: string }) {
  const def = towers.find((t) => t.id === id);
  if (!def) return null;
  return (
    <>
      {def.classes.map((c) => (
        <span key={c} className="disc__class" style={{ color: CLASS_COLORS[c].color }}>
          {getClass(c).name}
        </span>
      ))}
    </>
  );
}

function TowerPage({ id }: { id: string }) {
  const def = towers.find((t) => t.id === id);
  if (!def) return null;
  return (
    <div className="disc__page">
      <p>{def.description}</p>
      {/* The merge payoff, which is the number a player actually wants when deciding
          whether to chase a third copy of something. */}
      <p className="muted">
        Merge · ×{def.starScaling[1]} damage at ★2, ×{def.starScaling[2]} at ★3 · range ×
        {def.rangeScaling[2]} at ★3 · tier {def.tier}
      </p>
    </div>
  );
}

function EnemyPage({ id }: { id: string }) {
  const def = getEnemy(id);
  const brief = enemyBrief(def, (other) => getEnemy(other).name);
  return (
    <div className="disc__page">
      <p className="muted">{brief.stats}</p>
      {brief.traits.map((trait) => (
        <p key={trait}>· {trait}</p>
      ))}
    </div>
  );
}
